import React, { useState, useMemo, useRef } from 'react';
import { Table, Radio, Input, Button, Select, Checkbox, Dropdown, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ElementsTable from "./elementTable";
import { typeMap4 } from '@/constants/dataMap';
import './DesignValue.less';
import { rebuildFormula } from '@/config/func';
import { convertStreamsToDescMap } from '@/config/dataMap';
import materialsDB from '@/constants/materialsDB';
import { descMap } from '@/constants/desc';


type UnitType = 'mole_frac' | 'mass_conc' | 'molar_flow' | 'mass_flow';

interface Stream {
    name: string;
    description: string;
    molar_flow: number;
    mass_flow: number;
    t: number;
    pressure: number;
    phase?: string;
    gasification_rate?: number;
    enthalpy?: number;
    materials: {
        molar_flow: number[];
        mole_frac: number[];
    };
}

interface Material {
    cas: string;
    name: string;
}
interface DesignValueProps {
    values?: any;
    item?: any;
}
const DesignValue: React.FC<DesignValueProps> = ({ item }) => {
    // 1. 数据转换 + 自动计算 平均摩尔质量 & 总摩尔流量 & 组分摩尔流量
    const changeDesignValues = (data) => {
        let d = {};
        let ssss = [];
        const { streams, elements } = data.values.mix_input;

        // 构建 cas -> 分子量 映射表
        const casToMW = {};
        materialsDB.forEach(mat => {
            if (mat.cas) casToMW[mat.cas] = mat.MW;
        });

        const s0 = streams[0];
        d = {
            ...d,
            materials: s0.materials.map((i) => {
                return { cas: i.cas, name: i.name };
            }),
            elements,
        };

        streams.map((s) => {
            let singleStream = {};

            // ===================== 核心计算逻辑 =====================
            // 1. 获取当前流股的摩尔分率 mole_frac
            const mole_frac_arr = s.materials.map(i => i.molar_frac || 0);
            // 2. 获取对应物质的 cas 数组
            const cas_arr = s.materials.map(i => i.cas);
            // 3. 计算 平均摩尔质量 (kg/kmol)
            let avgMolarMass = 0;
            mole_frac_arr.forEach((mf, idx) => {
                const cas = cas_arr[idx];
                const mw = casToMW[cas] || 0;
                avgMolarMass += (mw / 1000) * mf;
            });
            // 4. 总摩尔流量 = 质量流量(kg/h) / 平均摩尔质量(kg/kmol)
            const mass_flow_val = s.params.f || 0;
            let molar_flow_val = avgMolarMass > 0 ? mass_flow_val / avgMolarMass : 0;
            // 5. 总摩尔流量 ÷ 1000（按你的要求修改）
            molar_flow_val = molar_flow_val / 1000;
            // 6. 计算 组分摩尔流量 = 总摩尔流量 × 摩尔分率
            const comp_molar_flow_arr = mole_frac_arr.map(mf => molar_flow_val * mf);
            // =======================================================

            singleStream = {
                ...singleStream,
                name: s.name,
                description: "-",
                t: s.params.t,
                t_unit: s.params.t_unit,
                pressure: s.params.p,
                p_unit: s.params.p_unit,
                molar_flow: molar_flow_val, // ✅ 总摩尔流量（÷1000）
                mass_flow: s.params.f,
                mass_flow_unit: s.params.f_unit,
                materials: {
                    mole_frac: s.materials.map((i) => i.molar_frac),
                    molar_flow: comp_molar_flow_arr, // ✅ 组分摩尔流量数组
                },
            };
            ssss.push(singleStream);
        });
        d = {
            ...d,
            streams: [...ssss],
        };
        return d;
    };

    let values: any = {};
    if (item.values.mix_input) {
        values = changeDesignValues(item);
    } else {
        values = item.values;
    }

    const [selectedValue, setSelectedValue] = useState('stream');
    const [unit, setUnit] = useState<UnitType>('mole_frac');
    const materials: Material[] = values.materials;
    const streams: Stream[] = values.streams;
    const elements = values.elements || [];

    // 列显示控制
    const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() => {
        // 默认显示所有基础列（不包括始终显示的 name 和 description）
        return ['description', 'molar_flow', 'mass_flow', 't', 'pressure'];
    });
    // 组分列显示控制
    const [showMaterialColumns, setShowMaterialColumns] = useState<boolean>(true);

    // 设备类型
    const availableElementTypes = useMemo(() => {
        const set = new Set<string>();
        elements.forEach((item) => set.add(item.type));
        return Array.from(set);
    }, [elements]);
    const [selectedElementType, setSelectedElementType] = useState<string>(availableElementTypes[0] || "");

    // 搜索高亮
    const [highlightedName, setHighlightedName] = useState<string | null>(null);
    const [searchType, setSearchType] = useState<'stream' | 'element' | null>(null);
    const [targetElementType, setTargetElementType] = useState<string | null>(null);
    const streamTableRef = useRef<HTMLDivElement>(null);
    const elementTableRef = useRef<HTMLDivElement>(null);

    function getStreamDescription(streamName: string): string {
        return descMap[streamName] || "-";
    }

    // 搜索选项
    const streamOptions = useMemo(() => {
        return streams?.map(s => ({
            label: `📊 流股 - ${s.name}`,
            value: s.name,
            type: 'stream' as const
        }));
    }, [streams]);

    const elementOptions = useMemo(() => {
        return elements.map(e => ({
            label: `⚙️ 设备 - ${e.name} (${typeMap4[e.type] || e.type})`,
            value: e.name,
            type: 'element' as const
        }));
    }, [elements, typeMap4]);

    const allSearchOptions = useMemo(() => {
        return [...elementOptions, ...streamOptions];
    }, [streamOptions, elementOptions]);

    // 只滚动表格自身的滚动容器：直接 scrollIntoView 会连带滚动抽屉/整页，把顶部区域推出去
    const scrollRowIntoView = (tr: HTMLElement) => {
        let scroller: HTMLElement | null = tr.parentElement;
        while (scroller && scroller !== document.body) {
            const oy = window.getComputedStyle(scroller).overflowY;
            if ((oy === 'auto' || oy === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
            scroller = scroller.parentElement;
        }
        if (!scroller || scroller === document.body) return;
        const trRect = tr.getBoundingClientRect();
        const boxRect = scroller.getBoundingClientRect();
        scroller.scrollTo({
            top: Math.max(
                0,
                scroller.scrollTop + (trRect.top - boxRect.top) - (scroller.clientHeight - trRect.height) / 2,
            ),
            behavior: 'smooth',
        });
    };

    // 搜索选中
    const handleSelect = (value: string, option: any) => {
        if (!value) {
            setHighlightedName(null);
            setSearchType(null);
            setTargetElementType(null);
            return;
        }
        const type = option?.type;
        if (type === 'stream' && selectedValue !== 'stream') {
            setSelectedValue('stream');
            setTargetElementType(null);
        } else if (type === 'element') {
            if (selectedValue !== 'element') {
                setSelectedValue('element');
            }
            const element = elements.find(e => e.name === value);
            if (element) {
                setTargetElementType(element.type);
            }
        }
        setHighlightedName(value);
        setSearchType(type);

        setTimeout(() => {
            const tr = document.querySelector(`tr[data-row-key="${value}"]`) as HTMLElement | null;
            if (tr) {
                scrollRowIntoView(tr);
                tr.classList.add('highlight-flash');
                setTimeout(() => {
                    tr.classList.remove('highlight-flash');
                }, 2000);
            }
        }, 300);
    };

    // 高亮行
    const rowClassName = (record: Stream | any) => {
        return highlightedName === record.name ? 'highlight-row' : '';
    };

    // 基础列
    const basicColumns: ColumnsType<Stream> = [
        {
            title: '流股',
            dataIndex: 'name',
            key: 'name',
            align: 'center',
            width: 100,
            fixed: 'left',
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            align: 'center',
            width: 200,
            render: (_, record) => getStreamDescription(record.name) || record.description,
        },
        {
            title: '摩尔流量（kmol/h）',
            key: 'molar_flow',
            align: 'center',
            width: 150,
            render: (_, record) => {
                const val = record.molar_flow;
                const displayVal = typeof val === 'number' && !isNaN(val) ? val.toFixed(4) : '-';
                return displayVal !== '-' && displayVal !== '0.0000' ? (
                    <span style={{ color: 'green' }}>{displayVal}</span>
                ) : (
                    displayVal
                );
            },
        },
        {
            title: '质量流量（kg/h）',
            dataIndex: 'mass_flow',
            key: 'mass_flow',
            align: 'center',
            width: 150,
            render: (val) => {
                const displayVal = val?.toFixed(2) || '-';
                return displayVal !== '-' && displayVal !== '0.00' ? (
                    <span style={{ color: 'green' }}>{displayVal}</span>
                ) : (
                    displayVal
                );
            },
        },
        {
            title: '温度（℃）',
            dataIndex: 't',
            key: 't',
            align: 'center',
            width: 100,
            render: (val) => {
                const displayVal = val?.toFixed(2) || '-';
                return displayVal !== '-' && displayVal !== '0.00' ? (
                    <span style={{ color: 'green' }}>{displayVal}</span>
                ) : (
                    displayVal
                );
            },
        },
        {
            title: '压力（MPa）',
            dataIndex: 'pressure',
            key: 'pressure',
            align: 'center',
            width: 100,
            render: (val) => {
                const displayVal = val?.toFixed(2) || '-';
                return displayVal !== '-' && displayVal !== '0.00' ? (
                    <span style={{ color: 'green' }}>{displayVal}</span>
                ) : (
                    displayVal
                );
            },
        },
    ];

    // 组分列（已自动显示组分摩尔流量）
    const materialColumns = useMemo(() => {
        return materials.map((mat, index) => ({
            title: rebuildFormula(mat.name),
            key: `material-${mat.name}`,
            align: 'center' as const,
            width: 90,
            render: (_: unknown, record: Stream) => {
                const { molar_flow, mole_frac } = record.materials;
                switch (unit) {
                    case 'mole_frac':
                        const moleFracVal = mole_frac[index];
                        // 原数据是什么就显示什么，不保留小数位
                        const displayMoleFrac = moleFracVal != null ? moleFracVal : '-';
                        return displayMoleFrac !== '-' && displayMoleFrac !== 0 ? (
                            <span style={{ color: 'green' }}>{displayMoleFrac}</span>
                        ) : (
                            displayMoleFrac
                        );
                    case 'molar_flow':
                        const molarFlowVal = molar_flow[index];
                        // 原数据是什么就显示什么，不保留小数位
                        const displayMolarFlow = molarFlowVal != null ? molarFlowVal : '-';
                        return displayMolarFlow !== '-' && displayMolarFlow !== 0 ? (
                            <span style={{ color: 'green' }}>{displayMolarFlow}</span>
                        ) : (
                            displayMolarFlow
                        );
                    default:
                        return '-';
                }
            },
        }));
    }, [materials, unit]);

    // 合并列（根据 visibleColumnKeys 过滤，name 列始终显示）
    const allColumns = useMemo(() => {
        // name 列始终显示
        const nameColumn = basicColumns.find(col => col.key === 'name');
        
        // 过滤其他基础列
        const filteredBasicColumns = basicColumns
            .filter(col => col.key !== 'name') // 排除 name 列
            .filter(col => visibleColumnKeys.includes(col.key as string));
        
        // 组合列：name + 选中的基础列
        const finalBasicColumns = [nameColumn!, ...filteredBasicColumns];
        
        // 组分列作为整体可选
        if (showMaterialColumns) {
            return [...finalBasicColumns, {
                title: `组分${unit === 'mole_frac' ? '摩尔分率' : '摩尔流量 （kmol/h）'}`,
                children: materialColumns
            }];
        }
        
        return finalBasicColumns;
    }, [basicColumns, materialColumns, visibleColumnKeys, showMaterialColumns, unit]);

    return (
        <div className="design-value-container" style={{ height: '100%' }}>
            <div className="fixed-header">
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                    <div className="tab-button-group">
                        <span style={{ fontSize: '20px', fontWeight: 'bold', lineHeight: '34px', marginRight: '10px' }}>{`${item?.type === "design" ? "设计值" : "运行数据"} ${item?.id}`}：</span>
                        <button
                            className={`tab-button ${selectedValue === 'element' ? 'active' : ''}`}
                            onClick={() => setSelectedValue('element')}
                        >
                            <span className={`tab-button-text ${selectedValue === 'element' ? 'active' : ''}`}>单元操作</span>
                        </button>
                        <button
                            className={`tab-button ${selectedValue === 'stream' ? 'active' : ''}`}
                            onClick={() => setSelectedValue('stream')}
                        >
                            <span className={`tab-button-text ${selectedValue === 'stream' ? 'active' : ''}`}>流股</span>
                        </button>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 14, color: '#666' }}>搜索设备/流股：</span>
                        <Select
                            style={{ width: 280 }}
                            dropdownStyle={{ zIndex: 100003 }}
                            placeholder="搜索流股或设备"
                            showSearch
                            options={allSearchOptions}
                            onSelect={handleSelect}
                            allowClear
                            filterOption={(input, option) => {
                                const label = option?.label || '';
                                return label.toLowerCase().includes(input.toLowerCase());
                            }}
                        />
{selectedValue === 'stream' && (
    <Dropdown
                            overlay={
                                <div style={{ padding: 12, background: '#fff', minWidth: 200 }}>
                                    <div style={{ marginBottom: 8, fontWeight: 600 }}>选择显示列：</div>
                                    {/* <div style={{ marginBottom: 8, color: '#999', fontSize: 12 }}>ℹ️ 流股列始终显示</div> */}
                                    <Checkbox.Group
                                        value={visibleColumnKeys}
                                        onChange={(checkedValues) => setVisibleColumnKeys(checkedValues as string[])}
                                        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                                    >
                                        {basicColumns.filter(col => col.key !== 'name').map(col => (
                                            <Checkbox key={col.key} value={col.key as string}>
                                                {col.title as string}
                                            </Checkbox>
                                        ))}
                                    </Checkbox.Group>
                                    <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                                        <Checkbox
                                            checked={showMaterialColumns}
                                            onChange={(e) => setShowMaterialColumns(e.target.checked)}
                                        >
                                            显示组分列（{unit === 'mole_frac' ? '摩尔分率' : '摩尔流量'}）
                                        </Checkbox>
                                    </div>
                                    <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => {
                                                setVisibleColumnKeys(basicColumns.filter(c => c.key !== 'name').map(c => c.key as string));
                                                setShowMaterialColumns(true);
                                            }}
                                        >
                                            全选
                                        </Button>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => {
                                                setVisibleColumnKeys(['description']);
                                                setShowMaterialColumns(false);
                                            }}
                                        >
                                            仅显示基本信息
                                        </Button>
                                    </div>
                                </div>
                            }
                            trigger={['click']}
                            overlayStyle={{ zIndex: 100002 }}
                        >
                            <Button icon={<span style={{ fontSize: 16 }}>⚙️</span>}>
                                列设置
                            </Button>
                        </Dropdown>
)}
                        
                    </div>
                </div>

                {selectedValue === 'stream' && (
                    <div className="header-section">
                        <div className="unit-switch">
                            <span>单位切换：</span>
                            <Radio.Group
                                value={unit}
                                onChange={(e) => setUnit(e.target.value)}
                                buttonStyle="solid"
                                size="small"
                            >
                                <Radio.Button value="mole_frac">摩尔分率</Radio.Button>
                                <Radio.Button value="molar_flow">摩尔流量 (kmol/h)</Radio.Button>
                            </Radio.Group>
                        </div>
                    </div>
                )}

                {selectedValue === 'element' && availableElementTypes.length > 0 && (
                    <div className="header-section">
                        <div className="unit-switch">
                            <span>设备类型：</span>
                            <Radio.Group
                                value={selectedElementType}
                                onChange={(e) => setSelectedElementType(e.target.value)}
                                buttonStyle="solid"
                                size="small"
                            >
                                {availableElementTypes.map((type) => (
                                    <Radio.Button key={type} value={type}>
                                        {typeMap4[type] || type}
                                    </Radio.Button>
                                ))}
                            </Radio.Group>
                        </div>
                    </div>
                )}
            </div>

            <div className="scrollable-content">
                {selectedValue === 'element' && (
                    <div ref={elementTableRef}>
                        <ElementsTable
                            key={JSON.stringify(values)}
                            design_values={values}
                            highlightedName={highlightedName}
                            targetElementType={targetElementType}
                            selectedType={selectedElementType}
                            onTypeChange={setSelectedElementType}
                        />
                    </div>
                )}

                {selectedValue === 'stream' && (
                    <div ref={streamTableRef} className="table-scroll-wrapper">
                        <Table
                            rowKey="name"
                            columns={allColumns}
                            dataSource={streams}
                            pagination={false}
                            bordered
                            className="merge-table"
                            scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
                            rowClassName={rowClassName}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DesignValue;